'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { DocsLinks } from '@/components/how-it-works/docs-links';
import { ChartUpIcon, Coins01Icon, HourglassIcon, JusticeScale01Icon, ShieldUserIcon } from '@/components/ui/icon/glyphs';
import { InfoModal } from '@/components/ui/info-modal';
import { useManipulationModalStore } from '@/stores/manipulation-modal';

/**
 * Explains how a market's result is protected from manipulation — opened from
 * the underlined "Preventing manipulation" cross-link inside the market detail
 * and markets-feed how-it-works modals. The result is decided by the market
 * itself, automatically; nobody can buy or override the outcome.
 *
 * Store-driven and mounted once at the app-shell level so a single instance
 * serves every cross-link surface.
 */
export function ManipulationHowItWorksModal() {
  const $ = _c(65);
  const t = useTranslations("manipulation_how_it_works");
  const open = useManipulationModalStore(_temp);
  const setOpen = useManipulationModalStore(_temp2);
  let t0;
  if ($[0] !== t) {
    t0 = t("title");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t) {
    t1 = t("description");
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t) {
    t2 = t("got_it");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== setOpen) {
    t3 = () => setOpen(false);
    $[6] = setOpen;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2 || $[9] !== t3) {
    t4 = <InfoModal.Footer cta={t2} onClose={t3} />;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t) {
    t5 = t("no_referee_title");
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t) {
    t6 = t("no_referee_description");
    $[13] = t;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t5 || $[16] !== t6) {
    t7 = <InfoModal.IconItem icon={ShieldUserIcon} title={t5} description={t6} />;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t) {
    t8 = t("share_title");
    $[18] = t;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t) {
    t9 = t("share_description");
    $[20] = t;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t8 || $[23] !== t9) {
    t10 = <InfoModal.IconItem icon={HourglassIcon} title={t8} description={t9} />;
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t) {
    t11 = t("penalty_title");
    $[25] = t;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t) {
    t12 = t("penalty_description");
    $[27] = t;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t11 || $[30] !== t12) {
    t13 = <InfoModal.IconItem icon={Coins01Icon} title={t11} description={t12} />;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t) {
    t14 = t("cost_title");
    $[32] = t;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t) {
    t15 = t("cost_description");
    $[34] = t;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t14 || $[37] !== t15) {
    t16 = <InfoModal.IconItem icon={ChartUpIcon} title={t14} description={t15} />;
    $[36] = t14;
    $[37] = t15;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t) {
    t17 = t("public_title");
    $[39] = t;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== t) {
    t18 = t("public_description");
    $[41] = t;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== t17 || $[44] !== t18) {
    t19 = <InfoModal.IconItem icon={JusticeScale01Icon} title={t17} description={t18} />;
    $[43] = t17;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== t10 || $[47] !== t13 || $[48] !== t16 || $[49] !== t19 || $[50] !== t7) {
    t20 = <InfoModal.IconList>{t7}{t10}{t13}{t16}{t19}</InfoModal.IconList>;
    $[46] = t10;
    $[47] = t13;
    $[48] = t16;
    $[49] = t19;
    $[50] = t7;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== t) {
    t21 = t("callout");
    $[52] = t;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== t21) {
    t22 = <InfoModal.Callout>{t21}</InfoModal.Callout>;
    $[54] = t21;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <DocsLinks docsPath="/concepts/market-integrity" />;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== open || $[58] !== setOpen || $[59] !== t0 || $[60] !== t1 || $[61] !== t20 || $[62] !== t22 || $[63] !== t4) {
    t24 = <InfoModal open={open} onOpenChange={setOpen} size="lg" title={t0} description={t1} footer={t4}>{t20}{t22}{t23}</InfoModal>;
    $[57] = open;
    $[58] = setOpen;
    $[59] = t0;
    $[60] = t1;
    $[61] = t20;
    $[62] = t22;
    $[63] = t4;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  return t24;
}
function _temp2(s_0) {
  return s_0.setOpen;
}
function _temp(s) {
  return s.open;
}